import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { normalizeSearchInput, SEARCH_MAX_LENGTH } from "../../utils/searchInput";
import { formatInr } from "../../utils/formatAmount";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

/** Typing helper: at most 2 digits after the decimal. */
/** Up to 4 digits before decimal, up to 2 after (e.g. 1234.12). */
const sanitizeTripAdvanceAmount = (raw) => {
  const s = String(raw ?? "").replace(/[^0-9.]/g, "");
  const dot = s.indexOf(".");
  if (dot === -1) return s.slice(0, 4);
  const intPart = s.slice(0, dot).slice(0, 4);
  const decPart = s.slice(dot + 1).replace(/\./g, "").slice(0, 2);
  if (decPart.length > 0 || s.endsWith(".")) return `${intPart}.${decPart}`;
  return intPart;
};

const isValidTripAdvanceAmount = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return true;
  if (s.endsWith(".")) return false;
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(s)) return false;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0;
};

/** Returns null if empty; NaN if invalid; else rounded amount (2 dp). */
const parseTripAdvanceInput = (raw) => {
  const s = String(raw ?? "").trim().replace(/,/g, "");
  if (s === "") return null;
  if (!isValidTripAdvanceAmount(s)) return Number.NaN;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return Number.NaN;
  return Math.round(n * 100) / 100;
};

/** Pull array from common API shapes */
const extractArrayFromData = (data, preferredKeys) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return [];
  for (const k of preferredKeys) {
    if (Array.isArray(data[k])) return data[k];
  }
  return [];
};

const mapVehicleRow = (v) => {
  const id = v._id ?? v.id;
  const registrationNumber = String(
    v.registration_number ??
      v.vehicle_number ??
      v.number_plate ??
      v.plate_number ??
      ""
  ).trim();
  const label =
    registrationNumber ||
    v.name ||
    (id != null ? String(id) : "Vehicle");
  return { id: id != null ? String(id) : "", label, registrationNumber, raw: v };
};

/** Client-side filter: vehicle registration number */
const matchesVehicleSearch = (vehicle, rawQuery) => {
  const term = String(rawQuery ?? "").trim().toLowerCase();
  if (!term) return true;
  const reg = String(vehicle.registrationNumber ?? "").toLowerCase();
  if (reg.includes(term)) return true;
  return String(vehicle.label ?? "").toLowerCase().includes(term);
};

const mapDriverRow = (d) => {
  const id = d._id ?? d.id;
  return {
    id: id != null ? String(id) : "",
    name: d.full_name ?? d.name ?? "—",
    mobile: String(d.mobile_number ?? d.mobile ?? "").trim(),
    driverRefId: String(d.driver_ref_id ?? "").trim(),
    license:
      d.driver_license_number ??
      d.license_number ??
      d.licence_number ??
      d.license ??
      "—",
    raw: d,
  };
};

const formatDriverMobile = (driver) => {
  if (!driver) return "";
  const num = String(driver.mobile ?? "").trim();
  if (!num) return "";
  const code = String(driver.raw?.country_code ?? "").trim();
  return code ? `${code} ${num}` : num;
};

/** Client-side filter: driver name, mobile, or licence number */
const matchesDriverSearch = (driver, rawQuery) => {
  const term = String(rawQuery ?? "").trim().toLowerCase();
  if (!term) return true;

  const name = String(driver.name ?? "").toLowerCase();
  if (name.includes(term)) return true;

  const license = String(driver.license ?? "").toLowerCase();
  if (license && license !== "—" && license.includes(term)) return true;

  const termDigits = term.replace(/\D/g, "");
  if (termDigits) {
    const mobileDigits = String(driver.mobile ?? "").replace(/\D/g, "");
    if (mobileDigits.includes(termDigits)) return true;
    if (license && license !== "—" && license.replace(/\D/g, "").includes(termDigits)) {
      return true;
    }
  }

  return false;
};

const driverInitials = (name) => {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const LICENSE_INPUT_CLASS =
  "form-control !mb-0 h-[52px] min-h-[52px] border-2 border-gray-200 !rounded-xl bg-gray-50 text-gray-700 shadow-none";

const DRIVER_ASSIGN_GRID =
  "grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_13.5rem_11rem] gap-x-4 gap-y-2";

const PICKER_BUTTON_CLASS =
  "flex h-[52px] min-h-[52px] max-h-[52px] w-full items-center justify-between gap-2 overflow-hidden rounded-xl border-2 bg-white px-3 text-left transition";

const DriverSearchPicker = ({
  pickerId,
  labelId,
  label,
  required = false,
  hideLabel = false,
  value,
  selectedDriver,
  onSelect,
  onClear,
  excludeDriverId = "",
  disabled = false,
  placeholder = "Select a driver",
  searchPlaceholder = "Search by name, mobile or licence number",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const pickerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 400);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDrivers = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        setDrivers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.listOfAllDriver}?search=`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
          toast.error(getErrorMessage(payload, response));
          setDrivers([]);
          return;
        }

        const raw = Array.isArray(payload)
          ? payload
          : extractArrayFromData(payload?.data, [
              "driver_data",
              "drivers",
              "data",
              "list",
            ]);
        setDrivers(raw.map(mapDriverRow).filter((r) => r.id));
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error("Could not load drivers.");
          setDrivers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDrivers();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectOptions = useMemo(() => {
    let list = drivers.filter((d) => matchesDriverSearch(d, debouncedQ));
    if (excludeDriverId) {
      list = list.filter((d) => d.id !== excludeDriverId);
    }
    if (value && selectedDriver?.id === value && !list.some((d) => d.id === value)) {
      list = [selectedDriver, ...list];
    }
    return list;
  }, [drivers, debouncedQ, excludeDriverId, value, selectedDriver]);

  const pickDriver = (driver) => {
    if (!driver?.id) return;
    onSelect(driver);
    setOpen(false);
    setQuery("");
  };

  const clearDriver = () => {
    onClear();
    setQuery("");
  };

  return (
    <div className="relative w-full" ref={pickerRef}>
      {!hideLabel ? (
        <label className="mb-2 block text-sm font-UbuntuMedium text-gray-800" id={labelId}>
          {label}
          {required ? <span className="text-danger"> *</span> : null}
        </label>
      ) : null}

      <button
        type="button"
        id={pickerId}
        aria-labelledby={labelId}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className={`${PICKER_BUTTON_CLASS} ${
          open ? "border-primary/50 ring-2 ring-primary/15" : "border-gray-200 hover:border-gray-300"
        } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
      >
        {selectedDriver ? (
          <span className="flex min-w-0 flex-1 items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#edf3f8] text-xs font-UbuntuMedium text-primary"
              aria-hidden
            >
              {driverInitials(selectedDriver.name)}
            </span>
            <span className="min-w-0 truncate font-UbuntuMedium text-[15px] text-gray-900">
              {selectedDriver.name}
            </span>
          </span>
        ) : (
          <span className="truncate text-gray-400 text-[15px]">{placeholder}</span>
        )}
        <span
          className={`shrink-0 text-xs text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        >
          ▼
        </span>
      </button>

      <div className="mt-2 min-h-[20px]">
        {selectedDriver ? (
          <button
            type="button"
            onClick={clearDriver}
            disabled={disabled}
            className="text-sm text-gray-500 underline-offset-2 hover:text-danger hover:underline disabled:opacity-50"
          >
            Clear selection
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5"
          role="presentation"
        >
          <div className="border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white p-3">
            <input
              type="search"
              className="form-control searchIcon !mb-0 !rounded-lg !border-gray-200 !shadow-sm focus:!border-primary/40"
              placeholder={searchPlaceholder}
              value={query}
              maxLength={SEARCH_MAX_LENGTH}
              onChange={(e) => setQuery(normalizeSearchInput(e.target.value))}
              autoComplete="off"
              autoFocus
              aria-controls={`${pickerId}-listbox`}
            />
          </div>

          <ul
            id={`${pickerId}-listbox`}
            role="listbox"
            aria-labelledby={labelId}
            className="max-h-56 overflow-y-auto py-1"
          >
            {loading ? (
              <li className="px-4 py-6 text-center text-sm text-gray-500">
                <span className="inline-block h-4 w-4 animate-pulse rounded-full bg-gray-200 mr-2 align-middle" />
                Loading drivers…
              </li>
            ) : selectOptions.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-gray-500">
                {query.trim() ? "No drivers match your search." : "No drivers available."}
              </li>
            ) : (
              selectOptions.map((d) => {
                const isSelected = value === d.id;
                return (
                  <li key={d.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => pickDriver(d)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] transition ${
                        isSelected ? "bg-primary/5 text-gray-900" : "text-gray-800 hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-UbuntuMedium ${
                          isSelected ? "bg-primary text-white" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {driverInitials(d.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-UbuntuMedium">{d.name}</span>
                        {d.license !== "—" ? (
                          <span className="block truncate text-xs text-gray-500">{d.license}</span>
                        ) : null}
                      </span>
                      {isSelected ? (
                        <span className="shrink-0 text-xs font-medium text-primary">Selected</span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const VehicleSearchPicker = ({
  pickerId,
  labelId,
  label,
  required = false,
  hideLabel = false,
  value,
  selectedVehicle,
  onSelect,
  onClear,
  disabled = false,
  placeholder = "Select a vehicle for this trip",
  searchPlaceholder = "Search by registration_number",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const pickerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 400);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadVehicles = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        setVehicles([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.listOfAllVehicle}?search=`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
          toast.error(getErrorMessage(payload, response));
          setVehicles([]);
          return;
        }

        const raw = extractArrayFromData(payload?.data, [
          "vehicle_data",
          "vehicles",
          "data",
          "list",
        ]);
        setVehicles(raw.map(mapVehicleRow).filter((r) => r.id));
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error("Could not load vehicles.");
          setVehicles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadVehicles();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectOptions = useMemo(() => {
    let list = vehicles.filter((v) => matchesVehicleSearch(v, debouncedQ));
    if (value && selectedVehicle?.id === value && !list.some((v) => v.id === value)) {
      list = [selectedVehicle, ...list];
    }
    return list;
  }, [vehicles, debouncedQ, value, selectedVehicle]);

  const pickVehicle = (vehicle) => {
    if (!vehicle?.id) return;
    onSelect(vehicle);
    setOpen(false);
    setQuery("");
  };

  const clearVehicle = () => {
    onClear();
    setQuery("");
  };

  return (
    <div className="relative w-full" ref={pickerRef}>
      {!hideLabel ? (
        <label className="mb-2 block text-sm font-UbuntuMedium text-gray-800" id={labelId}>
          {label}
          {required ? <span className="text-danger"> *</span> : null}
        </label>
      ) : null}

      <button
        type="button"
        id={pickerId}
        aria-labelledby={labelId}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className={`${PICKER_BUTTON_CLASS} ${
          open ? "border-primary/50 ring-2 ring-primary/15" : "border-gray-200 hover:border-gray-300"
        } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
      >
        {selectedVehicle ? (
          <span className="flex min-w-0 flex-1 items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#edf3f8] text-xs font-UbuntuMedium text-primary"
              aria-hidden
            >
              {String(selectedVehicle.label).slice(0, 2).toUpperCase()}
            </span>
            <span className="min-w-0 truncate font-UbuntuMedium text-[15px] text-gray-900">
              {selectedVehicle.label}
            </span>
          </span>
        ) : (
          <span className="truncate text-gray-400 text-[15px]">{placeholder}</span>
        )}
        <span
          className={`shrink-0 text-xs text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        >
          ▼
        </span>
      </button>

      <div className="mt-2 min-h-[20px]">
        {selectedVehicle ? (
          <button
            type="button"
            onClick={clearVehicle}
            disabled={disabled}
            className="text-sm text-gray-500 underline-offset-2 hover:text-danger hover:underline disabled:opacity-50"
          >
            Clear selection
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5"
          role="presentation"
        >
          <div className="border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white p-3">
            <input
              type="search"
              className="form-control searchIcon !mb-0 !rounded-lg !border-gray-200 !shadow-sm focus:!border-primary/40"
              placeholder={searchPlaceholder}
              value={query}
              maxLength={SEARCH_MAX_LENGTH}
              onChange={(e) => setQuery(normalizeSearchInput(e.target.value))}
              autoComplete="off"
              autoFocus
              aria-controls={`${pickerId}-listbox`}
            />
          </div>

          <ul
            id={`${pickerId}-listbox`}
            role="listbox"
            aria-labelledby={labelId}
            className="max-h-56 overflow-y-auto py-1"
          >
            {loading ? (
              <li className="px-4 py-6 text-center text-sm text-gray-500">
                <span className="inline-block h-4 w-4 animate-pulse rounded-full bg-gray-200 mr-2 align-middle" />
                Loading vehicles…
              </li>
            ) : selectOptions.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-gray-500">
                {query.trim() ? "No vehicles match your search." : "No vehicles available."}
              </li>
            ) : (
              selectOptions.map((v) => {
                const isSelected = value === v.id;
                return (
                  <li key={v.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => pickVehicle(v)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] transition ${
                        isSelected ? "bg-primary/5 text-gray-900" : "text-gray-800 hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-UbuntuMedium ${
                          isSelected ? "bg-primary text-white" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {String(v.label).slice(0, 2).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-UbuntuMedium">{v.label}</span>
                      {isSelected ? (
                        <span className="shrink-0 text-xs font-medium text-primary">Selected</span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const AddNewTrip = () => {
  const navigate = useNavigate();
  const [submitLoading, setSubmitLoading] = useState(false);
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const routeIdParam = searchParams.get("route_id")?.trim() || "";
  const [routeId, setRouteId] = useState(routeIdParam);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(!!routeIdParam);

  useEffect(() => {
    if (routeIdParam) setRouteId(routeIdParam);
  }, [routeIdParam]);

  const [vehicleId, setVehicleId] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [primaryDriverId, setPrimaryDriverId] = useState("");
  const [selectedPrimaryDriver, setSelectedPrimaryDriver] = useState(null);
  const [secondaryDriverId, setSecondaryDriverId] = useState("");
  const [selectedSecondaryDriver, setSelectedSecondaryDriver] = useState(null);
  const [advanceAmount, setAdvanceAmount] = useState("");

  useEffect(() => {
    setSubheading("Add New Trip");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    if (!routeId) {
      setDetail(null);
      setDetailLoading(false);
      return;
    }

    let cancelled = false;

    const loadDetail = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setDetailLoading(false);
        return;
      }

      setDetailLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.viewRouteDetails}?route_id=${encodeURIComponent(
        routeId
      )}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
          toast.error(getErrorMessage(payload, response));
          setDetail(null);
          return;
        }

        const data = payload?.data;
        setDetail(data && typeof data === "object" ? data : null);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load route details."
          );
          setDetail(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [routeId]);

  const summary = useMemo(() => {
    if (!detail) return null;
    const company = detail.company_id;
    const statusNum = detail.status === 1 || detail.status === "1" ? 1 : 0;
    return {
      routeRef:
        detail.route_ref_id != null && detail.route_ref_id !== ""
          ? `${detail.route_ref_id}`
          : "—",
      companyName: company?.name ?? "—",
      companyLocation: company?.company_location_id?.name ?? "—",
      miplLocation: detail.mipl_location_id?.name ?? "—",
      idealAverage:
        detail.ideal_average != null && !Number.isNaN(Number(detail.ideal_average))
          ? `${Number(detail.ideal_average)} average`
          : "—",
      tripAmount: formatInr(detail.trip_amount),
      singleDriver: formatInr(detail.single_trip_amount),
      dualDriver: formatInr(detail.dual_driver_amount),
      statusLabel: statusNum === 1 ? "Active" : "Inactive",
    };
  }, [detail]);

  const backToViewHref = routeId
    ? `/trip-assignment-management/view-trip?route_id=${encodeURIComponent(routeId)}`
    : "/trip-assignment-management";

  const validateTripForm = () => {
    if (!routeId) {
      toast.error("Missing route. Open this page from a route in the list.");
      return false;
    }
    if (!vehicleId.trim()) {
      toast.error("Please select a vehicle.");
      return false;
    }
    if (!primaryDriverId.trim()) {
      toast.error("Please select a primary driver.");
      return false;
    }
    return true;
  };

  const handleNextClick = () => {
    if (!validateTripForm()) return;

    const advanceParsed = parseTripAdvanceInput(advanceAmount);
    if (advanceParsed !== null && Number.isNaN(advanceParsed)) {
      toast.error("Enter amount up to 4 digits (e.g. 1234 or 1234.12).");
      return;
    }

    void submitAddTrip();
  };

  const submitAddTrip = async () => {
    if (!validateTripForm()) return;

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    const advanceParsed = parseTripAdvanceInput(advanceAmount);
    if (advanceParsed !== null && Number.isNaN(advanceParsed)) {
      toast.error("Enter amount up to 4 digits (e.g. 1234 or 1234.12).");
      return;
    }

    setSubmitLoading(true);
    const url = `${ACCOUNTANT_API}${endpoint.addTrip}`;
    const body = {
      route_id: routeId,
      vehicle_id: vehicleId,
      primary_driver_id: primaryDriverId,
    };
    if (secondaryDriverId.trim()) {
      body.secondary_driver_id = secondaryDriverId;
    }
    if (advanceParsed != null && advanceParsed > 0) {
      body.trip_advance = {
        fingerprint_signature_driver_id: primaryDriverId,
        advance_amount: advanceParsed,
      };
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const payload = await parseBody(response);

      if (!response.ok) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      toast.success(payload?.message || "Trip added successfully.");
      navigate(
        `/trip-assignment-management/view-trip?route_id=${encodeURIComponent(routeId)}`,
        { replace: true }
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitLoading(false);
    }
  };
  const viewTripHref = useMemo(() => {
    if (!routeId) return "/trip-assignment-management";
    return `/trip-assignment-management/view-trip?route_id=${encodeURIComponent(routeId)}`;
  }, [routeId]);
  return (
    <>
      <div className="mt-7">
      <div className='col-md-4'>
      <Link to={viewTripHref}  className="btn btn-primary shrink-0">
            Back
          </Link>       
           </div>
        {!routeId ? (
          <div className="rounded-lg px-4 py-6 bg-white mb-4 shadow-sm text-center text-gray-600">
            No route selected.{" "}
            <Link to="/trip-assignment-management" className="text-primary underline">
              Choose a route
            </Link>
            .
          </div>
        ) : detailLoading ? (
          <div className="rounded-lg px-4 py-8 bg-white mb-4 shadow-sm text-center text-gray-500">
            Loading route details…
          </div>
        ) : !summary ? (
          <div className="rounded-lg px-4 py-6 bg-white mb-4 shadow-sm text-center text-gray-600">
            Could not load route details.{" "}
            <Link to="/trip-assignment-management" className="text-primary underline">
              Back to list
            </Link>
          </div>
        ) : (
          <>
          <div className="rounded-lg px-4 py-2 bg-white mb-4 shadow-sm !mt-5">
            <ul className="grid grid-cols-2 [&_li]:flex [&_li]:py-1.5 [&_li]:justify-between [&_li:nth-child(odd)]:pr-12 [&_li:nth-child(odd)]:border-r [&_li:nth-child(even)]:pl-12 font-UbuntuMedium [&_.label]:text-gray-400">
              <li>
                <div className="label">Route ID</div>
                <div className="font-UbuntuMedium">{summary.routeRef}</div>
              </li>
              <li>
                <div className="label">Status</div>
                <div className="font-UbuntuMedium">{summary.statusLabel}</div>
              </li>
              <li>
                <div className="label">Company Name</div>
                <div className="font-UbuntuMedium">{summary.companyName}</div>
              </li>
              <li>
                <div className="label">Ideal Average</div>
                <div className="font-UbuntuMedium">{summary.idealAverage}</div>
              </li>
              <li>
                <div className="label">Company Location</div>
                <div className="font-UbuntuMedium">{summary.companyLocation}</div>
              </li>
              <li>
                <div className="label">MIPL Location</div>
                <div className="font-UbuntuMedium">{summary.miplLocation}</div>
              </li>
              {/* <li>
                <div className="label">Trip Amount</div>
                <div className="font-UbuntuMedium">{summary.tripAmount}</div>
              </li> */}
              <li>
                <div className="label">Single Driver</div>
                <div className="font-UbuntuMedium">{summary.singleDriver}</div>
              </li>
              <li>
                <div className="label">Dual Driver</div>
                <div className="font-UbuntuMedium">{summary.dualDriver}</div>
              </li>
            </ul>
          </div>

        <div className="border border-gray-200/90 !p-5 md:!p-6 bg-white rounded-xl font-UbuntuRegular mb-4 shadow-sm">
          <h2 className="text-lg font-UbuntuMedium my-0 text-gray-900 mb-4">Assign Trip</h2>

          <div className="max-w-3xl grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_13.5rem] gap-x-4 gap-y-2">
            <label
              className="block text-sm font-UbuntuMedium text-gray-800 leading-5 sm:col-start-1 sm:row-start-1"
              id="trip-vehicle-label"
              htmlFor="trip-vehicle"
            >
              Vehicle <span className="text-danger">*</span>
            </label>

            <div className="min-w-0 self-start sm:col-start-1 sm:row-start-2">
              <VehicleSearchPicker
                pickerId="trip-vehicle"
                labelId="trip-vehicle-label"
                label="Vehicle"
                required
                hideLabel
                value={vehicleId}
                selectedVehicle={selectedVehicle}
                onSelect={(vehicle) => {
                  setVehicleId(vehicle.id);
                  setSelectedVehicle(vehicle);
                }}
                onClear={() => {
                  setVehicleId("");
                  setSelectedVehicle(null);
                }}
                disabled={submitLoading}
                searchPlaceholder="Search by registration_number"
              />
            </div>
          </div>
        </div>

        <div className="border border-gray-200/90 !p-5 md:!p-6 bg-white rounded-xl font-UbuntuRegular mb-4 shadow-sm">
          <h2 className="text-lg font-UbuntuMedium my-0 text-gray-900 mb-4">Assigned Driver</h2>

          <div className="flex flex-col gap-8 max-w-4xl">
            <div className={DRIVER_ASSIGN_GRID}>
              <label
                id="primary-driver-label"
                className="block text-sm font-UbuntuMedium text-gray-800 leading-5 sm:col-start-1 sm:row-start-1"
                htmlFor="primary-driver"
              >
                Primary Driver <span className="text-danger">*</span>
              </label>
              <label
                className="hidden sm:block text-sm font-UbuntuMedium text-gray-800 leading-5 sm:col-start-2 sm:row-start-1"
                htmlFor="primary-license"
              >
                License Number
              </label>
              <label
                className="hidden sm:block text-sm font-UbuntuMedium text-gray-800 leading-5 sm:col-start-3 sm:row-start-1"
                htmlFor="primary-mobile"
              >
                Mobile Number
              </label>
              <div className="min-w-0 self-start sm:col-start-1 sm:row-start-2">
                <DriverSearchPicker
                  hideLabel
                  pickerId="primary-driver"
                  labelId="primary-driver-label"
                  label="Primary Driver"
                  required
                  value={primaryDriverId}
                  selectedDriver={selectedPrimaryDriver}
                  disabled={submitLoading}
                  placeholder="Select primary driver"
                  onSelect={(driver) => {
                    setPrimaryDriverId(driver.id);
                    setSelectedPrimaryDriver(driver);
                    if (secondaryDriverId === driver.id) {
                      setSecondaryDriverId("");
                      setSelectedSecondaryDriver(null);
                    }
                  }}
                  onClear={() => {
                    setPrimaryDriverId("");
                    setSelectedPrimaryDriver(null);
                  }}
                />
              </div>
              <div className="self-start sm:col-start-2 sm:row-start-2">
                <label
                  className="mb-2 block text-sm font-UbuntuMedium text-gray-800 sm:hidden"
                  htmlFor="primary-license"
                >
                  License Number
                </label>
                <input
                  id="primary-license"
                  value={selectedPrimaryDriver?.license ?? ""}
                  readOnly
                  disabled
                  className={LICENSE_INPUT_CLASS}
                />
                <div className="mt-2 min-h-[20px]" aria-hidden />
              </div>
              <div className="self-start sm:col-start-3 sm:row-start-2">
                <label
                  className="mb-2 block text-sm font-UbuntuMedium text-gray-800 sm:hidden"
                  htmlFor="primary-mobile"
                >
                  Mobile Number
                </label>
                <input
                  id="primary-mobile"
                  value={formatDriverMobile(selectedPrimaryDriver)}
                  readOnly
                  disabled
                  className={LICENSE_INPUT_CLASS}
                />
                <div className="mt-2 min-h-[20px]" aria-hidden />
              </div>
            </div>

            <div className={DRIVER_ASSIGN_GRID}>
              <label
                id="secondary-driver-label"
                className="block text-sm font-UbuntuMedium text-gray-800 leading-5 sm:col-start-1 sm:row-start-1"
                htmlFor="secondary-driver"
              >
                Secondary Driver (Optional)
              </label>
              <label
                className="hidden sm:block text-sm font-UbuntuMedium text-gray-800 leading-5 sm:col-start-2 sm:row-start-1"
                htmlFor="secondary-license"
              >
                License Number
              </label>
              <label
                className="hidden sm:block text-sm font-UbuntuMedium text-gray-800 leading-5 sm:col-start-3 sm:row-start-1"
                htmlFor="secondary-mobile"
              >
                Mobile Number
              </label>
              <div className="min-w-0 self-start sm:col-start-1 sm:row-start-2">
                <DriverSearchPicker
                  hideLabel
                  pickerId="secondary-driver"
                  labelId="secondary-driver-label"
                  label="Secondary Driver (Optional)"
                  value={secondaryDriverId}
                  selectedDriver={selectedSecondaryDriver}
                  excludeDriverId={primaryDriverId}
                  disabled={submitLoading}
                  placeholder="Select secondary driver"
                  onSelect={(driver) => {
                    setSecondaryDriverId(driver.id);
                    setSelectedSecondaryDriver(driver);
                  }}
                  onClear={() => {
                    setSecondaryDriverId("");
                    setSelectedSecondaryDriver(null);
                  }}
                />
              </div>
              <div className="self-start sm:col-start-2 sm:row-start-2">
                <label
                  className="mb-2 block text-sm font-UbuntuMedium text-gray-800 sm:hidden"
                  htmlFor="secondary-license"
                >
                  License Number
                </label>
                <input
                  id="secondary-license"
                  value={selectedSecondaryDriver?.license ?? ""}
                  readOnly
                  disabled
                  className={LICENSE_INPUT_CLASS}
                />
                <div className="mt-2 min-h-[20px]" aria-hidden />
              </div>
              <div className="self-start sm:col-start-3 sm:row-start-2">
                <label
                  className="mb-2 block text-sm font-UbuntuMedium text-gray-800 sm:hidden"
                  htmlFor="secondary-mobile"
                >
                  Mobile Number
                </label>
                <input
                  id="secondary-mobile"
                  value={formatDriverMobile(selectedSecondaryDriver)}
                  readOnly
                  disabled
                  className={LICENSE_INPUT_CLASS}
                />
                <div className="mt-2 min-h-[20px]" aria-hidden />
              </div>
            </div>
          </div>
        </div>
        <h2 className="text-lg font-UbuntuRegular my-0">Add Trip Advance</h2>
        <div className="grid grid-cols-4 gap-x-4 font-UbuntuRegular">
          <div className="form-group mt-2">
            <label className="mb-1 block" htmlFor="advance-amount">
              Enter Amount
            </label>
            <input
              id="advance-amount"
              type="text"
              className="form-control"
              value={advanceAmount}
              onChange={(e) => setAdvanceAmount(sanitizeTripAdvanceAmount(e.target.value))}
              inputMode="decimal"
              maxLength={7}
              title="Up to 4 digits before decimal and 2 after"
              autoComplete="off"
            />
          </div>
        </div>
      <div className="flex gap-x-3 items-center mt-4 col-md-12 font-UbuntuRegular">
        <Link
          to={backToViewHref}
          className="z-10 px-4 border-[1px] !border-primary !bg-primary !py-3 !rounded-full text-[15px] md:min-w-32 text-center text-white transition-all"
        >
          Cancel
        </Link>
        <button
          type="button"
          onClick={handleNextClick}
          disabled={detailLoading || submitLoading}
          className="z-10 border-[2px] !border-danger !bg-danger px-4 !py-3 md:py-[10px] rounded-full text-center text-[15px] md:min-w-32 text-white disabled:opacity-60"
        >
          Next
        </button>
      </div>
          </>
        )}
      </div>

      <Toastify />
    </>
  );
};

export default AddNewTrip;
